import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { adminOrderService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { formatDateTime } from '../utils/dateFormat'
import PaymentInformationSection from '../components/orders/PaymentInformationSection'
import PaymentProofEntriesSection from '../components/orders/PaymentProofEntriesSection'
import PaymentProofVerifyDialog from '../components/orders/PaymentProofVerifyDialog'
import PaymentProofEditAfterRejectDialog from '../components/orders/PaymentProofEditAfterRejectDialog'
import AdminDepositLinkOrderField from '../components/orders/AdminDepositLinkOrderField'
import DepositTimelineSection from '../components/orders/DepositTimelineSection'
import useAuthStore from '../store/authStore'
import { hasAdminRole, ADMIN_ROLE_CODES, hasAdminPermission } from '../utils/adminPermissions'

const PAYMENT_PROOF_REJECT_REASON_MAX = 500

export default function AdminDepositDetailPage() {
  const { requestId: requestIdParam } = useParams()
  const requestId = parseInt(requestIdParam || '', 10)
  const navigate = useNavigate()
  const adminRoleCodes = useAuthStore((s) => s.adminRoleCodes)
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const isSuperuserActor = useAuthStore((s) => s.isSuperuserActor)
  const canVerifyPaymentProof = hasAdminRole(
    adminRoleCodes,
    ADMIN_ROLE_CODES.PAYMENT_VERIFICATION,
  )
  const canEditRejectedPaymentProof = hasAdminPermission(
    adminPermissions,
    'order',
    'read',
  )
  const canLinkDepositToOrder = hasAdminPermission(
    adminPermissions,
    'order',
    'startProcurement',
  )

  const [linkOrderId, setLinkOrderId] = useState('')
  const [linkOrderError, setLinkOrderError] = useState(null)
  const [linkOrderSubmitting, setLinkOrderSubmitting] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deposit, setDeposit] = useState(null)
  const [paymentProofFeedback, setPaymentProofFeedback] = useState(null)
  const [paymentProofVerifyTarget, setPaymentProofVerifyTarget] = useState(null)
  const [paymentProofEditTarget, setPaymentProofEditTarget] = useState(null)
  const [paymentProofRejectTarget, setPaymentProofRejectTarget] = useState(null)
  const [paymentProofRejectReason, setPaymentProofRejectReason] = useState('')
  const [paymentProofRejectError, setPaymentProofRejectError] = useState(null)
  const [paymentProofRejectSubmitting, setPaymentProofRejectSubmitting] = useState(false)
  const [paymentProofRequestActionId, setPaymentProofRequestActionId] = useState(null)
  const [paymentProofReverseTarget, setPaymentProofReverseTarget] = useState(null)
  const [paymentProofReverseReason, setPaymentProofReverseReason] = useState('')
  const [paymentProofReverseError, setPaymentProofReverseError] = useState(null)
  const [paymentProofReverseSubmitting, setPaymentProofReverseSubmitting] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)

  const load = useCallback(async () => {
    if (!Number.isFinite(requestId)) return
    try {
      setLoading(true)
      setError(null)
      const data = await adminOrderService.getAdminDepositDetail(requestId)
      setDeposit(data)
    } catch (err) {
      setDeposit(null)
      setError(getApiErrorMessage(err, 'Failed to load deposit'))
    } finally {
      setLoading(false)
    }
  }, [requestId])

  useEffect(() => {
    load()
  }, [load])

  const paymentProofDetail = deposit?.payment_proof_detail
  const paymentProofRequestsSorted = useMemo(() => {
    const list = paymentProofDetail?.requests
    if (!Array.isArray(list)) return []
    return [...list].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [paymentProofDetail?.requests])
  const structuredProofs = paymentProofDetail?.proofs ?? []

  const bankAccountBySettingsId = useMemo(() => {
    const map = {}
    for (const proof of structuredProofs) {
      const id = proof?.shwapno_payment_settings_id
      const label = proof?.shwapno_receiving_account_label
      if (id != null && label) {
        map[String(id)] = { id, label }
      }
    }
    return map
  }, [structuredProofs])

  const closePaymentProofRejectDialog = () => {
    if (paymentProofRejectSubmitting) return
    setPaymentProofRejectTarget(null)
    setPaymentProofRejectReason('')
    setPaymentProofRejectError(null)
  }

  const closePaymentProofReverseDialog = () => {
    if (paymentProofReverseSubmitting) return
    setPaymentProofReverseTarget(null)
    setPaymentProofReverseReason('')
    setPaymentProofReverseError(null)
  }

  const handleVerifyPaymentProofRequest = (reqId) => {
    const req = paymentProofRequestsSorted.find((r) => r.id === reqId)
    if (!req) return
    setPaymentProofVerifyTarget(req)
  }

  const handleConfirmVerifyPaymentProofRequest = async (payload) => {
    if (!paymentProofVerifyTarget?.id) return
    try {
      setPaymentProofRequestActionId(paymentProofVerifyTarget.id)
      setPaymentProofFeedback(null)
      if (deposit?.order_id) {
        await adminOrderService.verifyPaymentProofRequest(
          deposit.order_id,
          paymentProofVerifyTarget.id,
          payload,
        )
      } else {
        await adminOrderService.verifyPaymentProofRequestById(
          paymentProofVerifyTarget.id,
          payload,
        )
      }
      setPaymentProofVerifyTarget(null)
      setPaymentProofFeedback({
        severity: 'success',
        message: 'Payment proof request verified.',
      })
      await load()
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to verify payment proof request')
      throw new Error(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRequestActionId(null)
    }
  }

  const handleConfirmRejectPaymentProofRequest = async () => {
    if (!paymentProofRejectTarget?.id) return
    const reason = paymentProofRejectReason.trim()
    if (!reason) {
      setPaymentProofRejectError('Rejection reason is required.')
      return
    }
    try {
      setPaymentProofRejectSubmitting(true)
      setPaymentProofRejectError(null)
      if (deposit?.order_id) {
        await adminOrderService.rejectPaymentProofRequest(
          deposit.order_id,
          paymentProofRejectTarget.id,
          reason,
        )
      } else {
        await adminOrderService.rejectPaymentProofRequestById(
          paymentProofRejectTarget.id,
          reason,
        )
      }
      closePaymentProofRejectDialog()
      setPaymentProofFeedback({
        severity: 'success',
        message: 'Payment proof request rejected.',
      })
      await load()
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to reject payment proof request')
      setPaymentProofRejectError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofRejectSubmitting(false)
    }
  }

  const handleConfirmReversePaymentProofRequest = async () => {
    if (!paymentProofReverseTarget?.id) return
    const reason = paymentProofReverseReason.trim()
    if (!reason) {
      setPaymentProofReverseError('Reversal reason is required.')
      return
    }
    try {
      setPaymentProofReverseSubmitting(true)
      setPaymentProofReverseError(null)
      if (deposit?.order_id) {
        await adminOrderService.reversePaymentProofRequest(
          deposit.order_id,
          paymentProofReverseTarget.id,
          reason,
        )
      } else {
        await adminOrderService.reversePaymentProofRequestById(
          paymentProofReverseTarget.id,
          reason,
        )
      }
      closePaymentProofReverseDialog()
      setPaymentProofFeedback({
        severity: 'success',
        message: 'Payment proof request reversed to pending.',
      })
      await load()
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to reverse payment proof request')
      setPaymentProofReverseError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setPaymentProofReverseSubmitting(false)
    }
  }

  const canReverse =
    isSuperuserActor &&
    (deposit?.order_id == null || deposit?.order_status === 'ACCEPTED')

  const showLinkOrder =
    canLinkDepositToOrder &&
    !deposit?.order_id &&
    deposit?.shop_id &&
    (deposit?.can_tag_order ||
      String(deposit?.status || '').toUpperCase() === 'PENDING')

  const handleLinkOrder = async () => {
    const orderIdNum = parseInt(String(linkOrderId || '').replace(/\D/g, ''), 10)
    if (!Number.isFinite(orderIdNum) || !requestId) {
      setLinkOrderError('Enter a valid order ID.')
      return
    }
    try {
      setLinkOrderSubmitting(true)
      setLinkOrderError(null)
      await adminOrderService.tagAdminShopDeposit(requestId, orderIdNum)
      setLinkOrderId('')
      setPaymentProofFeedback({
        severity: 'success',
        message: `Deposit linked to order #${orderIdNum}.`,
      })
      await load()
    } catch (err) {
      setLinkOrderError(getApiErrorMessage(err, 'Failed to link order'))
    } finally {
      setLinkOrderSubmitting(false)
    }
  }

  if (!Number.isFinite(requestId)) {
    return <Alert severity="error">Invalid deposit ID.</Alert>
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/orders/purchase/deposits')}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Deposits
        </Button>
      </Stack>

      {loading && !deposit ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : null}

      {deposit ? (
        <>
          <Card sx={{ p: 3, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              {deposit.deposit_id}
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Shop: {deposit.shop_name || deposit.shop_id || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Order:{' '}
                {deposit.order_id ? (
                  <Button
                    component={RouterLink}
                    to={`/orders/${deposit.order_id}`}
                    size="small"
                    sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                  >
                    #{deposit.order_id}
                  </Button>
                ) : (
                  'Not linked'
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {deposit.status} · Submitted {formatDateTime(deposit.submitted_at)}
              </Typography>
            </Stack>
            {showLinkOrder ? (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Link to purchase order
                </Typography>
                <AdminDepositLinkOrderField
                  organizationId={deposit.shop_id}
                  value={linkOrderId}
                  onChange={(id) => {
                    setLinkOrderId(id)
                    setLinkOrderError(null)
                  }}
                  disabled={linkOrderSubmitting}
                />
                {linkOrderError ? (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setLinkOrderError(null)}>
                    {linkOrderError}
                  </Alert>
                ) : null}
                <Button
                  variant="outlined"
                  disabled={linkOrderSubmitting || !String(linkOrderId || '').replace(/\D/g, '')}
                  onClick={handleLinkOrder}
                  sx={{ textTransform: 'none' }}
                >
                  {linkOrderSubmitting ? 'Linking…' : 'Link order'}
                </Button>
              </Box>
            ) : null}
          </Card>

          <PaymentInformationSection
            summary={paymentProofDetail?.summary}
            requests={paymentProofRequestsSorted}
            hasLegacyFiles={false}
            feedback={paymentProofFeedback}
            onDismissFeedback={() => setPaymentProofFeedback(null)}
            canVerify={canVerifyPaymentProof}
            actionRequestId={paymentProofRequestActionId}
            rejectSubmitting={paymentProofRejectSubmitting}
            onVerify={handleVerifyPaymentProofRequest}
            canEditRejected={canEditRejectedPaymentProof}
            onEditRejected={(req) => setPaymentProofEditTarget(req)}
            onReject={(req) => {
              setPaymentProofRejectTarget(req)
              setPaymentProofRejectReason('')
              setPaymentProofRejectError(null)
            }}
            canReverse={canReverse}
            reverseSubmitting={paymentProofReverseSubmitting}
            onReverse={(req) => {
              setPaymentProofReverseTarget(req)
              setPaymentProofReverseReason('')
              setPaymentProofReverseError(null)
            }}
          />

          <PaymentProofEntriesSection
            proofs={structuredProofs}
            legacyFiles={[]}
            bankAccountBySettingsId={bankAccountBySettingsId}
            canAddFile={false}
            onPreview={(url) => setPreviewUrl(url)}
          />

          <DepositTimelineSection events={deposit.timeline} />
        </>
      ) : null}

      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Payment proof</DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#111', textAlign: 'center' }}>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt=""
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <PaymentProofVerifyDialog
        open={Boolean(paymentProofVerifyTarget)}
        request={paymentProofVerifyTarget}
        onClose={() => {
          if (paymentProofRequestActionId) return
          setPaymentProofVerifyTarget(null)
        }}
        onConfirm={handleConfirmVerifyPaymentProofRequest}
        submitting={
          paymentProofVerifyTarget != null &&
          paymentProofRequestActionId === paymentProofVerifyTarget.id
        }
      />

      <PaymentProofEditAfterRejectDialog
        open={Boolean(paymentProofEditTarget)}
        orderId={deposit?.order_id ?? null}
        requestId={paymentProofEditTarget?.id}
        onClose={() => setPaymentProofEditTarget(null)}
        onSaved={async () => {
          setPaymentProofEditTarget(null)
          setPaymentProofFeedback({
            severity: 'success',
            message: 'Payment proof updated and resubmitted for verification.',
          })
          await load()
        }}
      />

      <Dialog open={Boolean(paymentProofRejectTarget)} onClose={closePaymentProofRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Reject payment proof request
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {paymentProofRejectError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{paymentProofRejectError}</Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reject request{' '}
            <strong>#{paymentProofRejectTarget?.id ?? '—'}</strong>? The shop or an admin can edit
            and resubmit the payment proof from the rejected queue or deposit detail.
          </Typography>
          <TextField
            autoFocus
            label="Rejection reason"
            value={paymentProofRejectReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setPaymentProofRejectReason(value)
                setPaymentProofRejectError(null)
              }
            }}
            fullWidth
            multiline
            rows={4}
            required
            margin="normal"
            disabled={paymentProofRejectSubmitting}
            placeholder="Enter reason for rejection…"
            helperText={`${paymentProofRejectReason.length}/${PAYMENT_PROOF_REJECT_REASON_MAX} characters`}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentProofRejectDialog} disabled={paymentProofRejectSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={paymentProofRejectSubmitting || !paymentProofRejectReason.trim()}
            onClick={handleConfirmRejectPaymentProofRequest}
          >
            Reject request
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentProofReverseTarget)} onClose={closePaymentProofReverseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reverse payment proof request</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {paymentProofReverseError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{paymentProofReverseError}</Alert>
          ) : null}
          <TextField
            autoFocus
            label="Reversal reason"
            value={paymentProofReverseReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setPaymentProofReverseReason(value)
                setPaymentProofReverseError(null)
              }
            }}
            fullWidth
            multiline
            rows={4}
            required
            disabled={paymentProofReverseSubmitting}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentProofReverseDialog} disabled={paymentProofReverseSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={paymentProofReverseSubmitting || !paymentProofReverseReason.trim()}
            onClick={handleConfirmReversePaymentProofRequest}
          >
            Reverse to pending
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
